import { ReactNode, useEffect, useRef, useState } from 'react';
import { Hidden, makeStyles } from '@material-ui/core';
import closeIcon from './../../assets/icons/close.svg';
import { haptic } from 'utils/haptics';

const useStyles = makeStyles((theme) => ({
  modal: {
    position: 'fixed',
    width: '100vw',
    height: '100vh',
    top: 0,
    left: 0,
    zIndex: 1050,
    animation: '$fade 0.2s',
    overflowY: 'auto',
    [theme.breakpoints.down('sm')]: {
      overflowY: 'hidden',
    },
  },
  modal__bg: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    position: 'fixed',
    zIndex: 1050,
    width: '100vw',
    height: '100vh',
  },
  modal__content: {
    backgroundColor: 'white',
    position: 'relative',
    margin: '60px auto',
    padding: 30,
    borderRadius: 16,
    zIndex: 1051,
    animation: '$slideUp 0.2s',
    [theme.breakpoints.down('sm')]: {
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      top: '10vh',
      borderRadius: '16px 16px 0 0',
      margin: 0,
      overflowY: 'scroll',
      overscrollBehavior: 'none',
      padding: 15,
    },
  },
  modal__closeIcon: {
    position: 'absolute',
    top: 20,
    right: 30,
    cursor: 'pointer',
    zIndex: 10,
    backgroundColor: 'white',
    padding: "0px 5px 2px 5px",
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: theme.palette.gray.g3,
    },
    "&:active": {
      transform: 'scale(0.96)',
    },
    [theme.breakpoints.down('sm')]: {
      top: 12,
      right: 12,
      backgroundColor: '#EBEBEB',
      width: 36,
      height: 36,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      '& img': {
        width: 14,
        height: 14,
      },
    },
  },
  modal__swipeHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#D0D0D0',
    margin: '4px auto 12px',
  },
  '@keyframes fade': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  '@keyframes slideUp': {
    '0%': {
      transform: 'translateY(200px)',
    },
    '100%': {
      transform: 'translateY(0)',
    },
  },
}));

const Modal = ({
  visible,
  children,
  close,
  closeButton,
  customWidth,
}: {
  visible: boolean;
  children?: ReactNode;
  close: () => void;
  closeButton?: boolean;
  customWidth?: number | string;
}) => {
  const classes = useStyles();
  const mobileContentRef = useRef<HTMLDivElement | null>(null);
  const [swipeY, setSwipeY] = useState(0);
  const [isSnapping, setIsSnapping] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const touchStartTime = useRef(0);
  const swipeXDelta = useRef(0);
  const isSwiping = useRef(false);

  // Haptic on open
  useEffect(() => {
    if (visible) {
      haptic('light');
    }
  }, [visible]);

  useEffect(() => {
    const element = document.getElementById('root');

    if (visible) {
      document.body.style.overflow = 'hidden';
      if (element) {
        element.style['overflow'] = 'hidden';
      }
    } else {
      document.body.style.overflow = 'unset';
      if (element) {
        element.style['overflow'] = 'unset';
      }
    }

    return () => {
      document.body.style.overflow = 'unset';
      if (element) {
        element.style['overflow'] = 'unset';
      }
    };
  }, [visible]);

  const canSwipeRef = useRef(false);

  const onTouchStart = (e: React.TouchEvent) => {
    const el = mobileContentRef.current;
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
    swipeXDelta.current = 0;
    isSwiping.current = false;

    // Don't allow swipe if the touch is inside a scrollable child that's been scrolled down
    let childScrolledDown = false;
    let node = e.target as HTMLElement | null;
    while (node && node !== el) {
      const style = window.getComputedStyle(node);
      const isScrollable = (style.overflowY === 'scroll' || style.overflowY === 'auto') && node.scrollHeight > node.clientHeight;
      if (isScrollable && node.scrollTop > 0) {
        childScrolledDown = true;
        break;
      }
      node = node.parentElement;
    }

    canSwipeRef.current = !childScrolledDown && !!(el && el.scrollTop <= 0);
  };

  // Attached as native listener with { passive: false } so preventDefault works
  const onTouchMoveRef = useRef<(e: TouchEvent) => void>();
  onTouchMoveRef.current = (e: TouchEvent) => {
    const el = mobileContentRef.current;
    if (!el) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    swipeXDelta.current = Math.abs(e.touches[0].clientX - touchStartX.current);

    if (isSwiping.current) {
      e.preventDefault();
      setSwipeY(delta);
    } else if (canSwipeRef.current && delta - swipeXDelta.current * 1.5 > 30) {
      // Commit to swiping — disable scroll so content doesn't move too
      isSwiping.current = true;
      el.style.overflowY = 'hidden';
      // Reset reference point so the modal starts at 0 instead of jumping
      touchStartY.current = e.touches[0].clientY;
      setSwipeY(0);
    } else if (delta < -5) {
      canSwipeRef.current = false;
    }
  };

  const touchMoveHandlerRef = useRef<(e: TouchEvent) => void>();
  const mobileContentCallbackRef = (el: HTMLDivElement | null) => {
    // Clean up previous listener
    if (mobileContentRef.current && touchMoveHandlerRef.current) {
      mobileContentRef.current.removeEventListener('touchmove', touchMoveHandlerRef.current);
    }
    mobileContentRef.current = el;
    if (el) {
      el.scrollTop = 0;
      const handler = (e: TouchEvent) => onTouchMoveRef.current?.(e);
      touchMoveHandlerRef.current = handler;
      el.addEventListener('touchmove', handler, { passive: false });
    }
  };

  const onTouchEnd = () => {
    const el = mobileContentRef.current;
    if (el) el.style.overflowY = '';

    if (!isSwiping.current) return;

    const elapsed = Date.now() - touchStartTime.current;
    const velocity = swipeY / elapsed; // px/ms

    if (swipeY > 100 || (swipeY > 20 && velocity > 0.4)) {
      // Animate sliding off screen, then close
      haptic('light');
      setIsDismissing(true);
      setSwipeY(window.innerHeight);
      setTimeout(() => {
        close();
        setSwipeY(0);
        setIsDismissing(false);
      }, 100);
    } else {
      setIsSnapping(true);
      setSwipeY(0);
      setTimeout(() => setIsSnapping(false), 200);
    }
    isSwiping.current = false;
  };

  const mobileSwipeStyle = swipeY > 0 || isSnapping ? {
    transform: `translateY(${swipeY}px)`,
    transition: (isSnapping || isDismissing) ? 'transform 0.10s ease-in' : 'none',
  } : undefined;

  return (
    <>
      {visible && (
        <div className={classes.modal}>
          <div className={classes.modal__bg} onClick={close} />
          <Hidden mdUp>
            <div
              ref={mobileContentCallbackRef}
              className={classes.modal__content}
              style={{ width: '100%', ...mobileSwipeStyle }}
              onTouchStart={onTouchStart}
              // onTouchMove attached via native listener in useEffect (passive: false)
              onTouchEnd={onTouchEnd}
            >
              <div className={classes.modal__swipeHandle} />
              {children}
              {closeButton && (
                <div className={classes.modal__closeIcon} onClick={close}>
                  <img alt={'X'} src={closeIcon} />
                </div>
              )}
            </div>
          </Hidden>
          <Hidden smDown>
            <div
              className={classes.modal__content}
              style={{ maxWidth: customWidth ? customWidth : 700, width: '100%' }}
            >
              {children}
              {closeButton && (
                <div className={classes.modal__closeIcon} onClick={close}>
                  <img alt={'X'} src={closeIcon} />
                </div>
              )}
            </div>
          </Hidden>
        </div>
      )}
    </>
  );
};
export default Modal;
